/*
 * Copyright © 2017-2021 Skale Education Inc. - All Rights Reserved
 */

import React, { useEffect, useState, useCallback, useRef } from "react";
import { useLazyQuery, useMutation } from "@apollo/client";
import { TextInput, View } from "react-native";
import { Prompt, Redirect } from "react-router";
import parse from "html-react-parser";
import ButtonBase from "@material-ui/core/ButtonBase";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import Fade from "@material-ui/core/Fade";
import { DragDropContext, Droppable } from "react-beautiful-dnd";
import Icon from "react-icons-kit";
import { exclamation } from "react-icons-kit/metrize/exclamation";
import { exclamationCircle } from "react-icons-kit/fa/exclamationCircle";

import { useDispatch, useSelector } from "react-redux";
import { ActionCreators } from "actions";
import { RootState } from "store/types";

import {
  BackExploreBar,
  Button,
  Checkbox,
  ConfirmDiscardModal,
  DescriptionTextField,
  LoadingView,
  Text,
  styles,
} from "UpduoLibrary";
import {
  skaleBackgroundBlue,
  skaleBlack,
  skaleGray,
  skaleLightGray,
  skalePrimaryBlue,
  skaleRed,
  skaleSummitDarkGray,
  skaleWebBackgroundLightGray,
  skaleWebBorderGray,
  skaleWebTextGray,
  skaleWhite,
  upduoPrimaryBlack,
} from "config/UpduoColors";
import PhonePreviewClassic from "./PhonePreviewClassic";
import SkillEditTab from "./SkillEditTab";
import SkillStepCardRow from "./SkillStepCardRow";
import { useEventListener } from "config/Hooks";
import { removeHTMLTag, reorder, processSkillString } from "config/Utilities";
import EditPublishedSkillModal from "./EditPublishedSkillModal";
import {
  GET_SKILL_DETAIL,
  GET_CURRICULUM_LIST,
} from "graphql/internal/Queries";
import { EDIT_SKILL, CREATE_SKILL } from "graphql/internal/Mutations";

import * as UpduoAnalytics from "config/UpduoAnalytics";

const defaultSessionSteps = [
  {
    title: "Card 1",
    minutes: 1,
    learner: {
      detail: "<b><h4>🧊 Icebreaker:</h4></b> <br>",
      heading:
        "👋 Greet your partner {PARTNER_FIRST_NAME}, then press MORE ⬇️.",
    },
    teacher: {
      detail: "<b><h4>🧊 Icebreaker:</h4></b> <br>",
      heading:
        "👋 Greet your partner {PARTNER_FIRST_NAME}, then press MORE ⬇️.",
    },
  },
  {
    title: "Card 2",
    minutes: 1,
    learner: {
      detail:
        "<b><h4>Consider:</h4></b> <br>🔹 Hints or ideas here<br>🔹<br>🔹",
      heading:
        "🗣 Share: QUESTION HERE. Then, ask {PARTNER_FIRST_NAME} to add to your answer.",
    },
    teacher: {
      detail:
        "<b><h4>➕Add to their answer with this information:</h4></b> <br>🔹 Discussion prompts or things to consider here<br>🔹<br>🔹",
      heading:
        "❓ Ask {PARTNER_FIRST_NAME}: “QUESTION HERE.” Listen for the information in MORE ⬇️.",
    },
  },
  {
    title: "Card 3",
    minutes: 1,
    learner: {
      detail: "<b><h4>Consider:</h4></b> <br>💡<br>💡<br>💡",
      heading:
        "🔄 Take turns sharing with {PARTNER_FIRST_NAME}: PROMPT HERE. Press MORE ⬇️ for ideas.",
    },
    teacher: {
      detail: "<b><h4>Consider:</h4></b> <br>💡<br>💡<br>💡",
      heading:
        "🔄 Take turns sharing with {PARTNER_FIRST_NAME}: PROMPT HERE. Press MORE ⬇️ for ideas.",
    },
  },
  {
    title: "Card 4",
    minutes: 1,
    learner: {
      detail:
        "<b><h4>Scenario:</h4></b> <br>🔹 Scenario details (keep it short) here<br>🔹<br>🔹",
      heading:
        "Read the scenario under MORE ⬇️, and problem solve with your partner. 🔄 Take turns sharing: How could you best respond to this situation?",
    },
    teacher: {
      detail:
        "<b><h4>Scenario:</h4></b> <br>🔹 Scenario details (keep it short) here<br>🔹<br>🔹",
      heading:
        "Read the scenario under MORE ⬇️, and problem solve with your partner. 🔄 Take turns sharing: How could you best respond to this situation?",
    },
  },
  {
    title: "Card 5",
    minutes: 1,
    learner: {
      detail: "",
      heading:
        "🤝 Thank your partner, and share one thing that you learned from them.",
    },
    teacher: {
      detail: "",
      heading:
        "🤝 Thank your partner, and share one thing that you learned from them.",
    },
  },
];

const roles = ["teacher", "learner"];
const maxNumSteps = 10;

export default function CurriculumDetailRoute({ match, history }) {
  // The skill detail/editing route component

  const dispatch = useDispatch();
  const authToken = useSelector((state: RootState) => state.authToken);
  const skillSessionSteps = useSelector(
    (state: RootState) => state.skillSessionSteps
  );
  const skillDescription = useSelector(
    (state: RootState) => state.skillDescription
  );
  const skillList = useSelector((state: RootState) => state.skillList);
  const skillName = useSelector((state: RootState) => state.skillName);
  const selfObject = useSelector((state: RootState) => state.selfObject);

  const [isLoading, setIsLoading] = useState(true);
  const [alertColor, setAlertColor] = useState("green");
  const [alertString, setAlertString] = useState("");
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [isDiscardModalVisible, setIsDiscardModalVisible] = useState(false);
  const [shownView, setShownView] = useState("description");
  const [previewIndex, setPreviewIndex] = useState(0);
  const [isPublished, setIsPublished] = useState(false);
  const [teacherTitle, setTeacherTitle] = useState("Partner");
  const [learnerTitle, setLearnerTitle] = useState("Partner");
  const [anchorEl, setAnchorEl] = useState(null);
  const [skillModuleList, setSkillModuleList] = useState<any[]>([]);
  const [editPublishedSkillModalVisible, setEditPublishedSkillModalVisible] =
    useState(false);
  const [editAlert, setEditAlert] = useState("");
  const [lessonGoalLength, setLessonGoalLength] = useState(0);

  const [skillModule, setSkillModule] = useState<any>(null);
  const skillModuleRef = useRef<any>();
  skillModuleRef.current = skillModule;

  // Hack for "library" organizations:
  // Only Content page for non-Maintainance users, plus Members page and editing only for Maintenance users:
  const libraryOrganizationIds =
    process.env.REACT_APP_LIBRARY_ORGANIZATION_IDS?.split(",") || [];
  const isLibraryOrganization = libraryOrganizationIds.includes(
    selfObject?.organizationId
  );
  const canEdit =
    (isLibraryOrganization && selfObject.storeName === "Maintenance") ||
    !isLibraryOrganization;
  const isNew = match.params.idHash === "new";

  const [getSkillDetail, { error: getSkillDetailError }] = useLazyQuery(
    GET_SKILL_DETAIL,
    {
      fetchPolicy: "network-only",
      onCompleted: ({ skill }) => {
        if (!skill) return;
        setIsPublished(skill.isPublished);
        setTeacherTitle(skill.titleTeacher);
        setLearnerTitle(skill.titleLearner);
        dispatch(
          ActionCreators.setSkillName(skill.name + (isNew ? " [COPY]" : ""))
        );
        dispatch(ActionCreators.setSkillDescription(skill.description));
        setLessonGoalLength(
          removeHTMLTag(skillDescription).replace(/\s*$/g, "").length
        );
        setSkillModule(skill.skillModule);
        // parse and extract detailTitle and detailContent from detail poperty (if non-empty) from sessionSteps[teacher] or sessionSteps[learner]
        const newSkillSessionSteps = JSON.parse(skill.sessionSteps);
        let newSkillStepDetail;
        for (let j = 0; j < newSkillSessionSteps.length; j++) {
          for (const role of roles) {
            newSkillStepDetail = _handleParseSessionStepDetail(
              newSkillSessionSteps[j][role].detail
            );
            newSkillSessionSteps[j][role] = Object.assign(
              {},
              newSkillSessionSteps[j][role],
              newSkillStepDetail
            );
          }
          newSkillSessionSteps[j].id =
            _handleGenerateUniqueSingleId(newSkillSessionSteps);
          newSkillSessionSteps[j].teacher.id =
            _handleGenerateUniqueSingleId(newSkillSessionSteps);
          newSkillSessionSteps[j].learner.id = _handleGenerateUniqueSingleId(
            newSkillSessionSteps,
            newSkillSessionSteps[j].teacher.id
          );
        }
        dispatch(ActionCreators.setSkillSessionSteps(newSkillSessionSteps));
        setIsLoading(false);
      },
    }
  );

  const getCurriculumListSharedQueryArguments = {
    onError: (error) => {
      console.error("ERROR getCurriculumList", error);
      UpduoAnalytics.logEvent("app-curriculumdetail-getcurriculumlist-error", {
        error,
      });
      history.goBack();
    },
  };

  const [getCurriculumListAndSetSkillModuleList, {}] = useLazyQuery(
    GET_CURRICULUM_LIST,
    {
      ...getCurriculumListSharedQueryArguments,
      fetchPolicy: "network-only",
      onCompleted: ({ staffSkillModules }) => {
        if (!staffSkillModules) {
          return;
        }
        const formattedCurriculumList = staffSkillModules.edges.map((edge) => {
          return {
            ...edge.node,
            activeSkillList: edge.node.activeSkillList.edges.map(
              (edge) => edge.node
            ),
          };
        });
        setSkillModuleList(formattedCurriculumList);
        setIsLoading(false);
      },
    }
  );

  const [getCurriculumListAndSetSkillModule, {}] = useLazyQuery(
    GET_CURRICULUM_LIST,
    {
      ...getCurriculumListSharedQueryArguments,
      fetchPolicy: "network-only",
      onCompleted: ({ staffSkillModules }) => {
        if (!staffSkillModules) {
          return;
        }
        if (staffSkillModules.edges.length === 0) {
          history.push("/content");
          return;
        }
        let skillModule =
          staffSkillModules.edges[staffSkillModules.edges.length - 1].node;
        skillModule = {
          ...skillModule,
          activeSkillList: skillModule.activeSkillList.edges.map(
            (edge) => edge.node
          ),
        };
        setSkillModule(skillModule);
        setIsLoading(false);
      },
    }
  );

  const [createSkill, {}] = useMutation(CREATE_SKILL, {
    fetchPolicy: "no-cache",
    onCompleted: () => {
      _handleFinishSave();
      history.push("/content");
    },
  });

  const [editSkill, {}] = useMutation(EDIT_SKILL, {
    fetchPolicy: "no-cache",
    onCompleted: ({ editSkillData }) => {
      const message = editSkillData?.message;
      if (message) {
        setEditPublishedSkillModalVisible(true);
        setIsLoading(false);
      } else {
        _handleFinishSave();
      }
    },
  });

  const [editSkillSummitConfirm, {}] = useMutation(EDIT_SKILL, {
    fetchPolicy: "no-cache",
    onCompleted: () => {
      setEditPublishedSkillModalVisible(false);
      _handleFinishSave();
    },
  });

  useEffect(() => {
    const defaultSkillSessionStepsObject = JSON.parse(
      JSON.stringify(defaultSessionSteps)
    );
    let newSkillStepDetail;
    for (let i = 0; i < defaultSessionSteps.length; i++) {
      for (const role of roles) {
        newSkillStepDetail = _handleParseSessionStepDetail(
          defaultSkillSessionStepsObject[i][role].detail
        );
        defaultSkillSessionStepsObject[i][role] = Object.assign(
          {},
          defaultSkillSessionStepsObject[i][role],
          newSkillStepDetail
        );
      }
      defaultSkillSessionStepsObject[i].id = _handleGenerateUniqueSingleId(
        defaultSkillSessionStepsObject
      );
      defaultSkillSessionStepsObject[i].teacher.id =
        _handleGenerateUniqueSingleId(defaultSkillSessionStepsObject);
      defaultSkillSessionStepsObject[i].learner.id =
        _handleGenerateUniqueSingleId(
          defaultSkillSessionStepsObject,
          defaultSkillSessionStepsObject[i].teacher.id
        );
    }
    dispatch(
      ActionCreators.setSkillSessionSteps(defaultSkillSessionStepsObject)
    );
    // Populate skill depending on whether skill is new, clone, or existing
    if (isNew) {
      dispatch(
        ActionCreators.setSkillDescription(
          "<ul><li>You and your partner will practice TOPIC HERE.</li></ul>"
        )
      );
      if (match.params.cloneIdHash) {
        _handleGetSkillDetail(match.params.cloneIdHash);
      } else {
        dispatch(
          ActionCreators.setSkillName("New Lesson (Metric: METRIC HERE)")
        );
        _handleGetSkillModuleName();
        setIsLoading(false);
      }
    } else {
      _handleGetSkillDetail(match.params.idHash);
    }
    _handleSetCardWidth();
    window.addEventListener("resize", _handleSetCardWidth);
    getCurriculumListAndSetSkillModuleList();

    return () => {
      window.removeEventListener("resize", _handleSetCardWidth);
    };
  }, [
    authToken,
    dispatch,
    isNew,
    match.params.cloneIdHash,
    match.params.idHash,
  ]);

  const _handleFinishSave = () => {
    setIsLoading(false);
    setAlertColor("green");
    setAlertString("Your changes are saved!");
  };

  const _handleGenerateUniqueSingleId = (
    usedSkillSessionSteps,
    additionalId = ""
  ) => {
    const result = Math.random().toString(36).substring(7);
    for (let i = 0; i < skillSessionSteps.length; i++) {
      if (
        skillSessionSteps[i].id === result ||
        skillSessionSteps[i].teacher.id === result ||
        skillSessionSteps[i].learner.id === result
      ) {
        return _handleGenerateUniqueSingleId(
          usedSkillSessionSteps,
          additionalId
        );
      }
    }
    if (additionalId && additionalId === result) {
      return _handleGenerateUniqueSingleId(usedSkillSessionSteps, additionalId);
    }
    return result;
  };

  const _handleDragEnd = useCallback(
    (result) => {
      if (!canEdit || !result.destination) {
        setEditAlert("Card editing is currently disabled.");
        return;
      }
      // Handles correcting ordering for skillSessionSteps,
      // and does so for only the local state
      if (
        result.type === "step" &&
        result.source.index !== result.destination.index
      ) {
        const newSkillSessionSteps = reorder(
          skillSessionSteps,
          result.source.index,
          result.destination.index
        );
        for (let k = 0; k < skillSessionSteps.length; k++) {
          newSkillSessionSteps[k].order = k;
          newSkillSessionSteps[k].title = `Card ${k + 1}`;
        }
        dispatch(ActionCreators.setSkillSessionSteps(newSkillSessionSteps));
        _handleShowUnsavedWarning();
      }
    },
    [skillSessionSteps, canEdit]
  );

  const _handleRowDelete = useCallback(
    (cardKey) => {
      const newSkillSessionSteps = JSON.parse(
        JSON.stringify(skillSessionSteps)
      );
      newSkillSessionSteps.splice(cardKey, 1);
      for (let k = cardKey; k < newSkillSessionSteps.length; k++) {
        newSkillSessionSteps[k].order = k;
        newSkillSessionSteps[k].title = `Card ${k + 1}`;
      }
      dispatch(ActionCreators.setSkillSessionSteps(newSkillSessionSteps));
      _handleShowUnsavedWarning();
    },
    [skillSessionSteps]
  );

  const _handleRowClone = useCallback(
    (cardKey) => {
      const clonedSkillSessionStep = JSON.parse(
        JSON.stringify(skillSessionSteps[cardKey])
      );
      clonedSkillSessionStep.id =
        _handleGenerateUniqueSingleId(skillSessionSteps);
      clonedSkillSessionStep.teacher.id =
        _handleGenerateUniqueSingleId(skillSessionSteps);
      clonedSkillSessionStep.learner.id = _handleGenerateUniqueSingleId(
        skillSessionSteps,
        clonedSkillSessionStep.teacher.id
      );
      clonedSkillSessionStep.order = skillSessionSteps.length;
      skillSessionSteps.push(clonedSkillSessionStep);
      const newSkillSessionSteps = reorder(
        JSON.parse(JSON.stringify(skillSessionSteps)) as any[],
        clonedSkillSessionStep.order,
        cardKey + 1
      );
      for (let k = cardKey; k < newSkillSessionSteps.length; k++) {
        newSkillSessionSteps[k].order = k;
        newSkillSessionSteps[k].title = `Card ${k + 1}`;
      }
      dispatch(ActionCreators.setSkillSessionSteps(newSkillSessionSteps));
      _handleShowUnsavedWarning();
    },
    [skillSessionSteps]
  );

  const _handleRowAdd = useCallback(() => {
    const newSkillSessionSteps = JSON.parse(JSON.stringify(skillSessionSteps));
    const newSkillSessionStep = {
      title: `Card ${skillSessionSteps.length + 1}`,
      order: skillSessionSteps.length,
      minutes: 1,
      id: "",
      learner: {
        id: "",
        detail: "",
        heading: "",
      },
      teacher: {
        id: "",
        detail: "",
        heading: "",
      },
    };
    newSkillSessionStep.id =
      _handleGenerateUniqueSingleId(newSkillSessionSteps);
    newSkillSessionStep.teacher.id =
      _handleGenerateUniqueSingleId(newSkillSessionSteps);
    newSkillSessionStep.learner.id = _handleGenerateUniqueSingleId(
      newSkillSessionSteps,
      newSkillSessionStep.teacher.id
    );
    newSkillSessionSteps.push(newSkillSessionStep);
    dispatch(ActionCreators.setSkillSessionSteps(newSkillSessionSteps));
    _handleShowUnsavedWarning();
  }, [skillSessionSteps]);

  const _handleShowUnsavedWarning = useCallback(() => {
    if (canEdit) {
      setAlertColor(skaleRed);
      setAlertString("You have unsaved changes.");
    }
  }, [canEdit]);

  const _handleSetCardWidth = () => {
    setWindowWidth(window.innerWidth);
  };

  const _handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const _handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const _handleChangeSkillModule = useCallback(
    (value) => {
      if (skillModuleRef.current.id !== value.id) {
        setSkillModule(value);
        _handleShowUnsavedWarning();
      }
      _handleClose();
    },
    [_handleShowUnsavedWarning, _handleClose]
  );

  const _handleGenerateMenuItem = useCallback(
    (key, value) => {
      return (
        <MenuItem
          key={key}
          value={value}
          style={{
            color: skaleWebTextGray,
            fontFamily: "ObjectSans-Regular",
            width: windowWidth * 0.27,
            height: 50,
            marginLeft: 0,
            borderWidth: 1,
          }}
          onClick={() => {
            _handleChangeSkillModule(value);
          }}
        >
          {value.name}
        </MenuItem>
      );
    },
    [_handleChangeSkillModule]
  );

  const _handleGenerateOptions = useCallback(() => {
    const allOptions: Object[] = [];
    let key = 0;
    let value = null;
    for (let i = 0; i < skillModuleList.length; i++) {
      if (skillModuleList[i] !== null && skillModuleList[i] !== undefined) {
        if (skillModuleList[i].isActive) {
          value = skillModuleList[i];
          allOptions.push(_handleGenerateMenuItem(key, value));
          key++;
        }
      }
    }
    return allOptions;
  }, [skillModuleList, _handleGenerateMenuItem]);

  const allMenuItems = _handleGenerateOptions();

  const _handleGetSkillModuleName = () => {
    setIsLoading(true);
    getCurriculumListAndSetSkillModule();
  };

  const _handleGetSkillDetail = (skillHash) => {
    setIsLoading(true);
    getSkillDetail({
      variables: {
        getSkillDetailRequest: { skillId: skillHash },
      },
    });
  };

  const _handleSetIsPublished = () => {
    setIsPublished((previousState) => !previousState);
    _handleShowUnsavedWarning();
  };

  const _handleParseSessionStepDetail = (sessionStepDetailString) => {
    if (!sessionStepDetailString) {
      return {};
    }
    const rawSplit = sessionStepDetailString.split("</h4></b>");
    const detailTitle = rawSplit[0].replace("<b><h4>", "");
    return { detailTitle, detailContent: rawSplit[1] };
  };

  const _handleStringifySessionStepDetail = (sessionStepRoleObject) => {
    if (
      !sessionStepRoleObject.detailTitle &&
      !sessionStepRoleObject.detailContent
    ) {
      return "";
    }
    const detailString = `<b><h4>${sessionStepRoleObject.detailTitle}</h4></b>${sessionStepRoleObject.detailContent}`;
    return detailString;
  };

  const _handleEditDescription = (newSkillDescription) => {
    if (newSkillDescription === skillDescription) {
      return;
    }
    _handleShowUnsavedWarning();
    dispatch(ActionCreators.setSkillDescription(newSkillDescription));
  };

  const _handleEditSessionSteps = useCallback(
    (editObject) => {
      const newSkillSessionSteps = JSON.parse(
        JSON.stringify(skillSessionSteps)
      );
      const newSessionStep = JSON.parse(
        JSON.stringify(editObject.skillSessionStep)
      );
      for (const role of roles) {
        newSessionStep[role]["detail"] = _handleStringifySessionStepDetail(
          newSessionStep[role]
        );
      }
      newSkillSessionSteps[editObject.cardKey] = newSessionStep;
      dispatch(ActionCreators.setSkillSessionSteps(newSkillSessionSteps));
      _handleShowUnsavedWarning();
    },
    [skillSessionSteps, dispatch, _handleShowUnsavedWarning]
  );

  const _handleSave = useCallback(() => {
    UpduoAnalytics.logEvent("user-skill-detail-save", {
      skillId: match.params.idHash,
    });
    const body = _handleGenerateSaveApiBody();
    if (isNew) {
      createSkill({ variables: { createSkillRequest: body } });
    } else {
      editSkill({
        variables: {
          editSkillRequest: { ...body, skillId: match.params.idHash },
        },
      });
    }
  }, [
    authToken,
    skillName,
    skillModule,
    isPublished,
    teacherTitle,
    learnerTitle,
    skillDescription,
    skillSessionSteps,
    skillList,
    history,
    isNew,
    match.params.idHash,
    skillModuleList,
  ]);

  const _handleKeyDown = useCallback(
    (e) => {
      const regex = /Mac/;
      const isMac = regex.exec(navigator.platform);
      if (e.keyCode === 83 && (isMac ? e.metaKey : e.ctrlKey)) {
        // Allow save keyboard shortcut to work
        e.preventDefault();
        _handleSave();
      } else if (shownView === "preview") {
        // Allow arrow keys to work for preview
        if (e.keyCode === 37 && previewIndex > 0) {
          // left arrow on preview screen
          e.preventDefault();
          setPreviewIndex((previousState) => previousState - 1);
        } else if (
          e.keyCode === 39 &&
          previewIndex < skillSessionSteps.length - 1
        ) {
          // right arrow on preview screen
          e.preventDefault();
          setPreviewIndex((previousState) => previousState + 1);
        }
      }
    },
    [shownView, previewIndex, skillSessionSteps, _handleSave]
  );

  useEventListener("keydown", _handleKeyDown);

  const _handleDiscardEditsButtonPress = () => {
    UpduoAnalytics.logEvent("user-skill-detail-edit-toggle", {
      skillId: match.params.idHash,
      isChanged: alertString ? true : false,
    });
    if (alertString) {
      setIsDiscardModalVisible(true);
      return;
    }
    if (isNew) {
      history.push("/content");
      return;
    }
    setAlertString("");
  };

  const _handleCancelDiscard = () => {
    UpduoAnalytics.logEvent("user-skill-detail-discard-cancel", {
      skillId: match.params.idHash,
    });
    setIsDiscardModalVisible(false);
  };

  const _handleConfirmDiscard = () => {
    UpduoAnalytics.logEvent("user-skill-detail-discard-confirm", {
      skillId: match.params.idHash,
    });
    if (isNew) {
      history.push("/content");
    } else {
      _handleGetSkillDetail(match.params.idHash);
      setAlertString("");
      setIsDiscardModalVisible(false);
    }
  };

  const _handleEditPublishedSkillModalDismiss = () => {
    UpduoAnalytics.logEvent("user-skill-unpublish-modal-dismiss", {
      skillId: match.params.idHash,
    });
    setEditPublishedSkillModalVisible(false);
  };

  const _handleGenerateSaveApiBody = useCallback(() => {
    if (!canEdit) {
      setAlertColor(skaleRed);
      setAlertString("Lesson editing is currently disabled.");
      return;
    }
    const descriptionIsInvalid = skillDescription === "<ul><li></li></ul>";
    let sessionStepIsInvalid, detailIsInvalid;
    for (let k = 0; k < skillSessionSteps.length; k++) {
      for (const role of roles) {
        detailIsInvalid =
          "detailTitle" in skillSessionSteps[k][role] &&
          "detailContent" in skillSessionSteps[k][role] &&
          (!skillSessionSteps[k][role].detailTitle ||
            skillSessionSteps[k][role].detailContent === "<ul><li></li></ul>");
        sessionStepIsInvalid =
          !skillSessionSteps[k][role].heading || detailIsInvalid;
      }
      if (!skillName || descriptionIsInvalid || sessionStepIsInvalid) {
        setAlertColor(skaleRed);
        setAlertString(
          "Empty fields are not allowed. Please add content to empty fields and/or delete unused cards."
        );
        return;
      }
    }
    const skillDescriptionString = skillDescription;
    const skillSessionStepsArray = JSON.parse(
      JSON.stringify(skillSessionSteps)
    );
    for (let j = 0; j < skillSessionSteps.length; j++) {
      for (const role of roles) {
        delete skillSessionStepsArray[j][role].detailTitle;
        delete skillSessionStepsArray[j][role].detailContent;
      }
    }

    let body = {
      name: skillName,
      isPublished: isPublished,
      titleTeacher: teacherTitle,
      titleLearner: learnerTitle,
      description: skillDescriptionString,
      sessionSteps: JSON.stringify(skillSessionStepsArray),
      skillModuleId: skillModule
        ? skillModule.id
        : skillModuleList[skillModuleList.length - 1]?.id,
    };
    setIsLoading(true);
    if (isNew) {
      const activeSkillList = skillList.filter((skill) => skill.isActive);
      body = Object.assign({}, body, { order: activeSkillList.length + 1 });
    }
    return body;
  }, [
    authToken,
    skillName,
    skillModule,
    isPublished,
    teacherTitle,
    learnerTitle,
    skillDescription,
    skillSessionSteps,
    skillList,
    history,
    isNew,
    match.params.idHash,
    skillModuleList,
  ]);

  const _handleSaveWithSummitConfirm = useCallback(() => {
    UpduoAnalytics.logEvent("user-skill-detail-save-curriculum", {
      skillId: match.params.idHash,
    });
    const body = _handleGenerateSaveApiBody();
    if (isNew) {
      createSkill({ variables: { createSkillRequest: body } });
    } else {
      editSkillSummitConfirm({
        variables: {
          editSkillRequest: {
            ...body,
            skillId: match.params.idHash,
            flag: "summit",
          },
        },
      });
    }
  }, [
    authToken,
    skillName,
    skillModule,
    isPublished,
    teacherTitle,
    learnerTitle,
    skillDescription,
    skillSessionSteps,
    skillList,
    history,
    isNew,
    match.params.idHash,
    skillModuleList,
    canEdit,
  ]);

  if (!selfObject.isStaff) {
    return <Redirect to={"/"} />;
  }

  if (getSkillDetailError) {
    console.error(getSkillDetailError);
    return (
      <View style={{ alignItems: "center" }}>
        {"Error fetching lesson detail... "}
      </View>
    );
  } else if (isLoading) {
    return <LoadingView />;
  } else {
    const saveButton = (
      <Button
        marginLeft={20}
        onPress={() => {
          _handleSave();
        }}
        width={200}
        height={36}
        hoverBorderWidth={1}
        borderColor={skalePrimaryBlue}
        text={isNew ? "Save New Lesson" : "Save Changes"}
      />
    );
    const editToggleButton = (
      <Button
        onPress={() => {
          _handleDiscardEditsButtonPress();
        }}
        width={170}
        height={36}
        borderWidth={1}
        borderColor={skaleRed}
        color={skaleWhite}
        textColor={skaleRed}
        text={"Discard Edits"}
      />
    );
    const addStepButton = (
      <Button
        text={"Add New Card"}
        textColor={
          skillSessionSteps.length < maxNumSteps ? skaleWhite : skaleGray
        }
        width={274}
        onPress={() => {
          UpduoAnalytics.logEvent("user-add-step", {
            skill: skillTitle,
          });
          _handleRowAdd();
        }}
        height={48}
        borderWidth={1}
        borderColor={
          skillSessionSteps.length < maxNumSteps ? skalePrimaryBlue : skaleGray
        }
        color={
          skillSessionSteps.length < maxNumSteps ? skalePrimaryBlue : skaleWhite
        }
        disabled={skillSessionSteps.length >= maxNumSteps}
      />
    );
    const skillEditingHeading = isNew ? "Create Lesson" : "Edit Lesson";
    const skillTitle = skillName ? skillName : "Untitled";
    const skillHeading = `${skillEditingHeading} - ${skillTitle}`;
    const editControls = (
      <View style={{ flexDirection: "row" }}>
        {editToggleButton}
        {saveButton}
      </View>
    );
    const descriptionView = (
      <View
        style={{
          width: "100%",
          minWidth: 1000,
          backgroundColor: skaleWhite,
          borderWidth: 1,
          borderColor: skaleWebBorderGray,
          borderRadius: 12,
          padding: 48,
        }}
      >
        <Text
          style={{
            fontSize: 24,
            fontWeight: "bold",
            color: skaleWebTextGray,
            marginBottom: 40,
          }}
        >
          {"Description"}
        </Text>
        <Text
          style={{
            color: skaleWebTextGray,
            marginBottom: 8,
          }}
        >
          {"Lesson Name"}
        </Text>
        <TextInput
          style={[
            styles.skillInput,
            { width: Math.min(810, windowWidth * 0.56) },
          ]}
          value={skillName}
          onChangeText={(text) => {
            dispatch(ActionCreators.setSkillName(text));
            _handleShowUnsavedWarning();
          }}
        />
        <View
          style={{
            width: Math.min(810, windowWidth * 0.56),
          }}
        >
          <View
            style={{
              flexDirection: "row",
              justifyContent: "space-between",
              marginTop: 30,
              marginBottom: 5,
            }}
          >
            <Text style={{ color: skaleWebTextGray }}>{"Lesson Goal"}</Text>
            <Text style={{ color: skaleWebBackgroundLightGray }}>{`${
              200 - lessonGoalLength
            } characters remaining`}</Text>
          </View>

          <DescriptionTextField
            description={skillDescription}
            onEdit={_handleEditDescription}
            maxLength={200}
            setContentLength={(length) => {
              setLessonGoalLength(length);
            }}
          />
        </View>
        <View
          style={{
            flexDirection: "row",
            marginTop: 32,
          }}
        >
          <View
            style={{
              width: windowWidth * 0.27,
              marginRight: 32,
            }}
          >
            <Text
              style={{
                color: skaleWebTextGray,
                marginBottom: 12,
              }}
            >
              {"Teacher Title: "}
            </Text>
            <TextInput
              style={styles.skillTitleInput}
              value={teacherTitle}
              onChangeText={(text) => {
                setTeacherTitle(text);
                _handleShowUnsavedWarning();
              }}
            />
          </View>
          <View
            style={{
              width: windowWidth * 0.27,
            }}
          >
            <Text
              style={{
                color: skaleWebTextGray,
                marginBottom: 12,
              }}
            >
              {"Learner Title: "}
            </Text>
            <TextInput
              style={styles.skillTitleInput}
              value={learnerTitle}
              onChangeText={(text) => {
                setLearnerTitle(text);
                _handleShowUnsavedWarning();
              }}
            />
          </View>
        </View>
        <View
          style={{
            flexDirection: "row",
            marginTop: 32,
            marginRight: 32,
            width: windowWidth * 0.27,
          }}
        >
          <View>
            <Text
              style={{
                color: skaleWebTextGray,
              }}
            >
              {"Choose Topic"}
            </Text>
            <View
              style={[
                styles.dropdown,
                {
                  width: windowWidth * 0.27,
                  marginVertical: 12,
                  height: 35,
                  marginLeft: 0,
                  marginTop: 10,
                  alignItems: "flex-start",
                  justifyContent: "center",
                  overflow: "hidden",
                },
              ]}
            >
              <ButtonBase
                aria-controls={"fade-menu"}
                aria-haspopup={"true"}
                onClick={_handleClick}
                style={{
                  alignItems: "flex-start",
                  width: windowWidth * 0.27,
                  marginLeft: -8.5,
                  color: skaleWebTextGray,
                }}
              >
                <Text
                  style={{
                    display: "flex",
                    flex: 1,
                    marginTop: 3,
                    alignItems: "flex-start",
                    color: skaleWebTextGray,
                    width: windowWidth * 0.27,
                    fontSize: 16,
                  }}
                >
                  {skillModule
                    ? skillModule.name
                    : skillModuleList[skillModuleList.length - 1]?.name}
                </Text>
              </ButtonBase>
              <Menu
                id={"fade-menu"}
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={_handleClose}
                TransitionComponent={Fade}
                style={{
                  marginLeft: -17.5,
                  marginTop: 1,
                  width: windowWidth * 0.27,
                }}
              >
                {allMenuItems}
              </Menu>
            </View>
          </View>
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              marginVertical: 30,
              width: windowWidth * 0.27,
            }}
          >
            <Checkbox checked={isPublished} onClick={_handleSetIsPublished} />
            <Text
              style={{
                color: skaleWebTextGray,
                fontWeight: "bold",
              }}
            >
              {"Published"}
            </Text>
          </View>
        </View>
      </View>
    );
    const sessionView = (
      <View
        style={{
          width: "100%",
          minWidth: 1000,
          backgroundColor: skaleWhite,
          borderWidth: 1,
          borderColor: skaleWebBorderGray,
          borderRadius: 12,
          padding: 48,
        }}
      >
        <Text
          style={{
            fontSize: 24,
            fontWeight: "bold",
            color: skaleWebTextGray,
            marginBottom: 40,
          }}
        >
          {"Lesson"}
        </Text>
        <View
          style={{
            flexDirection: "row",
          }}
        >
          <View style={{ flexGrow: 1 }}>
            <View style={styles.skillRoleHeading}>
              <Text
                style={{
                  color: skaleWebTextGray,
                  fontWeight: "bold",
                  fontSize: 18,
                }}
              >
                {`${teacherTitle}'s Session Cards`}
              </Text>
            </View>
          </View>
          <View style={{ width: 24 }}></View>
          <View style={{ flexGrow: 1 }}>
            <View style={styles.skillRoleHeading}>
              <Text
                style={{
                  color: skaleWebTextGray,
                  fontWeight: "bold",
                  fontSize: 18,
                }}
              >
                {`${learnerTitle}'s Session Cards`}
              </Text>
            </View>
          </View>
        </View>

        <View
          style={{
            width: "100%",
            height: "40px",
            borderRadius: 12,
            backgroundColor: skaleBackgroundBlue,
            flexDirection: "row",
            alignItems: "center",
            marginBottom: 20,
            paddingLeft: 14.67,
          }}
        >
          <Icon
            size={17}
            icon={exclamation}
            style={{
              paddingRight: 13.67,
              color: skalePrimaryBlue,
              marginTop: -4,
            }}
          />
          <Text
            style={{
              fontFamily: "ObjectSans-Regular",
              fontStyle: "normal",
              fontWeight: "normal",
              fontSize: 14,
              color: skalePrimaryBlue,
              flexDirection: "row",
            }}
          >
            {
              'Use the "{PARTNER_FIRST_NAME}" and “{LESSON_NAME}” placeholders in the header/detail input lines to make the real names appear in the lesson.'
            }
          </Text>
        </View>

        <DragDropContext onDragEnd={_handleDragEnd}>
          <Droppable droppableId={"step-droppable"} type={"step"}>
            {(provided) => (
              <div ref={provided.innerRef}>
                <View style={{ minHeight: 50 }}>
                  {skillSessionSteps.map((item, itemIndex) => (
                    <SkillStepCardRow
                      key={item.id}
                      cardKey={itemIndex}
                      skillSessionStep={skillSessionSteps[itemIndex]}
                      cardWidth={null}
                      minute={skillSessionSteps[itemIndex].minutes}
                      onEditSessionSteps={_handleEditSessionSteps}
                      onDelete={_handleRowDelete}
                      onClone={_handleRowClone}
                      isEnabledClone={skillSessionSteps.length < maxNumSteps}
                      isEnabledDelete={skillSessionSteps.length > 1}
                    />
                  ))}
                  {provided.placeholder}
                </View>
              </div>
            )}
          </Droppable>
        </DragDropContext>
        <View style={{ flexDirection: "column", alignItems: "center" }}>
          {addStepButton}
          <Text
            style={{
              fontSize: 16,
              marginLeft: 0,
              marginTop: 16,
              color: skaleBlack,
            }}
          >{`Maximum of ${maxNumSteps} steps`}</Text>
        </View>
        <Text style={{ color: skaleRed }}>{editAlert}</Text>
      </View>
    );
    const previewView = (
      <View
        style={{
          alignItems: "center",
          width: "100%",
          minWidth: 1000,
          backgroundColor: skaleWhite,
          borderWidth: 1,
          borderColor: skaleWebBorderGray,
          borderRadius: 12,
          padding: 48,
        }}
      >
        <View
          style={{
            alignSelf: "flex-start",
          }}
        >
          <Text
            style={{
              fontFamily: "ObjectSans-Medium",
              fontSize: 24,
              fontWeight: "bold",
              color: skaleBlack,
            }}
          >
            {"Preview"}
          </Text>
        </View>
        <View style={{ flexDirection: "row" }}>
          <PhonePreviewClassic
            title={"This is Alice's view"}
            isTeacher={true}
            cardIndex={previewIndex}
            onChangeIndex={(newIndex) => {
              setPreviewIndex(newIndex);
            }}
            skillSessionSteps={skillSessionSteps}
            teacherRole={teacherTitle}
            learnerRole={learnerTitle}
            skillName={skillName}
            communityName={selfObject.organizationName}
          />
          <View style={{ width: 150 }} />
          <PhonePreviewClassic
            title={"This is Bob's view"}
            isTeacher={false}
            cardIndex={previewIndex}
            skillSessionSteps={skillSessionSteps}
            teacherRole={teacherTitle}
            learnerRole={learnerTitle}
            skillName={skillName}
            communityName={selfObject.organizationName}
          />
        </View>
        <View
          style={{
            alignSelf: "flex-end",
            flexDirection: "row",
            justifyContent: "center",
            alignItems: "center",
            paddingTop: 20,
            paddingBottom: 100,
          }}
        >
          <Icon size={15} icon={exclamationCircle} />
          <Text style={{ paddingLeft: 10, color: skaleSummitDarkGray }}>
            {"Use left / right arrow keys to navigate the session cards."}
          </Text>
        </View>
        <View
          style={{
            borderBottomColor: skaleLightGray,
            borderBottomWidth: 1,
            alignSelf: "stretch",
          }}
        />
        <View
          style={{
            alignSelf: "flex-start",
            flexDirection: "column",
            paddingTop: 20,
          }}
        >
          <Text style={{ fontSize: 14, color: skaleWebTextGray, padding: 10 }}>
            {"Lesson Name"}
          </Text>
          <Text style={{ fontSize: 20, padding: 10, paddingBottom: 24 }}>
            {skillName}
          </Text>
          <Text style={{ fontSize: 14, color: skaleWebTextGray, padding: 10 }}>
            {"Lesson Goal"}
          </Text>
          <Text style={{ fontSize: 16, padding: 10 }}>
            {parse(
              processSkillString(
                skillDescription,
                undefined,
                undefined,
                undefined,
                undefined,
                skillName,
                undefined,
                selfObject.organizationName
              )
            )}
          </Text>
        </View>
      </View>
    );

    return (
      <View style={{ flex: 1 }}>
        <BackExploreBar
          previousTitle={"Content"}
          onBack={() => {
            UpduoAnalytics.logEvent("user-skill-detail-back", {
              skillId: match.params.idHash,
            });
            history.push("/content");
          }}
        />
        <View style={styles.routeBodyContainer}>
          <View
            style={{
              minWidth: 1000,
              zIndex: 5,
              paddingVertical: 10,
              justifyContent: "center",
            }}
          >
            <View
              style={{
                flexDirection: "row",
                alignItems: "flex-start",
                paddingHorizontal: 15,
                justifyContent: "space-between",
              }}
            >
              <Text
                style={{
                  fontSize: 24,
                  fontWeight: "bold",
                  color: upduoPrimaryBlack,
                  flexShrink: 1,
                  marginRight: 20,
                }}
              >
                {skillHeading}
              </Text>
              <View style={{ flexDirection: "row", alignItems: "center" }}>
                <Text
                  style={{
                    color: alertColor,
                    marginRight: 20,
                  }}
                >
                  {alertString}
                </Text>
                {alertString && alertColor === skaleRed ? editControls : null}
              </View>
            </View>
          </View>
          <View style={{ alignItems: "center" }}>
            <View style={{ flexDirection: "row", marginBottom: 32 }}>
              <SkillEditTab
                type={"description"}
                shownView={shownView}
                onPress={() => {
                  setShownView("description");
                }}
              />
              <SkillEditTab
                type={"session"}
                shownView={shownView}
                onPress={() => {
                  setShownView("session");
                }}
              />
              <SkillEditTab
                type={"preview"}
                shownView={shownView}
                onPress={() => {
                  setShownView("preview");
                }}
              />
            </View>
            {shownView === "description" ? descriptionView : null}
            {shownView === "session" ? sessionView : null}
            {shownView === "preview" ? previewView : null}
          </View>
        </View>
        <ConfirmDiscardModal
          visible={isDiscardModalVisible}
          onCancelDiscard={_handleCancelDiscard}
          onConfirmDiscard={_handleConfirmDiscard}
        />
        <Prompt
          when={alertColor === skaleRed && !!alertString}
          message={"You have unsaved changes, are you sure you want to leave?"}
        />
        <EditPublishedSkillModal
          onDismiss={() => _handleEditPublishedSkillModalDismiss()}
          onEditPublishedSkillConfirm={() => _handleSaveWithSummitConfirm()}
          isVisible={editPublishedSkillModalVisible}
          isDelete={false}
        />
      </View>
    );
  }
}
